import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { decodeTokenExpiration } from '../utils/tokenUtils';

const CHECK_INTERVAL = 60 * 1000;
const REFRESH_WINDOW = 5 * 60 * 1000;
const ACTIVE_WINDOW = 10 * 60 * 1000;
const IDLE_TIMEOUT = 6 * 60 * 60 * 1000; // 6 hours of inactivity requires re-auth
const ABSOLUTE_SESSION_MAX = 24 * 60 * 60 * 1000; // Hard cap for a single login session
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'focus'];

const SessionManager = () => {
    const { token, tokenExpiresAt, sessionStartedAt, isAuthenticated, refreshToken, logout } = useAuthStore();
    const location = useLocation();
    const lastActivityRef = useRef(Date.now());
    const refreshInProgressRef = useRef(false);
    const forcedLogoutRef = useRef(false);

    const markActivity = useCallback(() => {
        lastActivityRef.current = Date.now();
    }, []);

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        lastActivityRef.current = Date.now();

        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                markActivity();
            }
        };

        ACTIVITY_EVENTS.forEach((eventName) => {
            window.addEventListener(eventName, markActivity, { passive: true });
        });
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            ACTIVITY_EVENTS.forEach((eventName) => {
                window.removeEventListener(eventName, markActivity);
            });
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [isAuthenticated, markActivity]);

    const forceLogout = useCallback(async () => {
        if (forcedLogoutRef.current) {
            return;
        }

        forcedLogoutRef.current = true;
        try {
            await logout(true);
        } catch (error) {
            console.warn('Error during forced logout', error);
        } finally {
            window.location.href = '/login?expired=true';
        }
    }, [logout]);

    useEffect(() => {
        const tokenToCheck = token || localStorage.getItem('authToken');
        if (!tokenToCheck) {
            return;
        }

        const cachedExpiration = tokenExpiresAt ?? Number(localStorage.getItem('tokenExpiresAt'));
        const decodedExpiration = decodeTokenExpiration(tokenToCheck);
        const expiresAt = Number.isFinite(cachedExpiration) ? cachedExpiration : decodedExpiration;

        if (!expiresAt) {
            return;
        }

        const sessionStart = sessionStartedAt ?? Number(localStorage.getItem('sessionStartedAt'));
        if (sessionStart && Date.now() - sessionStart >= ABSOLUTE_SESSION_MAX) {
            forceLogout();
            return;
        }

        if (Date.now() >= expiresAt) {
            forceLogout();
        }
    }, [location.key, token, tokenExpiresAt, sessionStartedAt, forceLogout]);

    useEffect(() => {
        if (!isAuthenticated || !token || !tokenExpiresAt) {
            return undefined;
        }

        forcedLogoutRef.current = false;

        const checkSession = async () => {
            if (!isAuthenticated) {
                return;
            }

            const now = Date.now();
            const expiresIn = tokenExpiresAt - now;
            const timeSinceActivity = now - lastActivityRef.current;
            const sessionStart = sessionStartedAt ?? Number(localStorage.getItem('sessionStartedAt'));
            const sessionAge = sessionStart ? now - sessionStart : 0;
            const isUserActive = timeSinceActivity <= ACTIVE_WINDOW;

            if (sessionAge && sessionAge >= ABSOLUTE_SESSION_MAX) {
                await forceLogout();
                return;
            }

            if (timeSinceActivity >= IDLE_TIMEOUT) {
                await forceLogout();
                return;
            }

            if (expiresIn <= 0) {
                if (isUserActive && !refreshInProgressRef.current) {
                    refreshInProgressRef.current = true;
                    const refreshed = await refreshToken();
                    refreshInProgressRef.current = false;

                    if (!refreshed) {
                        await forceLogout();
                    } else {
                        lastActivityRef.current = Date.now();
                    }
                } else if (!refreshInProgressRef.current) {
                    await forceLogout();
                }

                return;
            }

            if (expiresIn <= REFRESH_WINDOW && isUserActive && !refreshInProgressRef.current) {
                refreshInProgressRef.current = true;
                const refreshed = await refreshToken();
                refreshInProgressRef.current = false;

                if (!refreshed) {
                    await forceLogout();
                } else {
                    lastActivityRef.current = Date.now();
                }
            }
        };

        const intervalId = window.setInterval(() => {
            checkSession().catch((error) => {
                console.warn('Session check failed', error);
            });
        }, CHECK_INTERVAL);

        checkSession().catch((error) => {
            console.warn('Initial session check failed', error);
        });

        return () => {
            window.clearInterval(intervalId);
            refreshInProgressRef.current = false;
        };
    }, [isAuthenticated, token, tokenExpiresAt, sessionStartedAt, refreshToken, forceLogout]);

    return null;
};

export default SessionManager;
